import React, { useCallback, useContext, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import "../../theme/AppStyles.css";

import { ProfileContext } from "../../context/ProfileContext";
import { AuthContext } from "../../context/AuthContext";
import { WerkerJobContext } from "../../context/JobContextWerker";
import { SchedJobContext } from "../../context/JobContextScheduler";
import { LoadingScreen } from "../../components/LoadingScreen";
import { WebLayout } from "../../components/layouts/WebLayout";
import CalendarView from "../../components/desktop/CalendarView";
import { JobDetailPanel } from "../../components/desktop/JobDetailPanel";
import ScheduleJob from "../ScheduleJob";
import SelectWerkers from "../SelectWerkers";
import SelectWerkersSeries from "../selectWerkersSeries";
import { CalendarJobResource, schedJobsToEvents, werkerJobsToEvents } from "../../utils/calendarUtils";

// ─── Types shared with the wrappers ──────────────────────────────────────────

type EditCompleteResult = {
  nextView?: "details" | "selectWerkers" | "selectWerkersSeries";
  jobId?: string;
  seriesId?: string;
  jobIds?: string[];
};

type WerkersCompleteResult = { nextView?: "details"; jobId?: string };

// ─── Main component ───────────────────────────────────────────────────────────

const CalendarDesktop: React.FC = () => {
  const { currentUser, authReady } = useContext(AuthContext);
  const { profile } = useContext(ProfileContext);
  const { schedJob, hasMorePast, earliestPastJobDate, loadMorePast } = useContext(SchedJobContext);
  const { werkerJob, invitedFeedJobs } = useContext(WerkerJobContext);

  const isScheduler = profile?.UserLastRoleScheduler === true;

  // ─── Right-pane state ─────────────────────────────────────────────────────
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [selectedIsScheduler, setSelectedIsScheduler] = useState<boolean>(isScheduler);

  // Edit / werker-select panes (mirrors HomeDesktop pattern)
  const [editingJobId, setEditingJobId] = useState<string | null>(null);
  const [scheduleJobPanelMode, setScheduleJobPanelMode] = useState<"edit" | null>(null);
  const [selectWerkersJobId, setSelectWerkersJobId] = useState<string | null>(null);
  const [selectWerkersSeriesState, setSelectWerkersSeriesState] = useState<{
    seriesId: string;
    jobIds?: string[];
  } | null>(null);

  // ─── Build calendar events ────────────────────────────────────────────────
  // Contexts return null until the first Firestore snapshot arrives; treat
  // null as "still loading" and [] as "loaded but empty".
  const isJobsLoading = isScheduler ? schedJob == null : werkerJob == null;

  const events = useMemo(() => {
    if (isScheduler) {
      return schedJobsToEvents(schedJob ?? []);
    }
    const combined = [...(invitedFeedJobs ?? []), ...(werkerJob ?? [])];
    return werkerJobsToEvents(combined);
  }, [isScheduler, schedJob, werkerJob, invitedFeedJobs]);

  // ─── Event handlers ───────────────────────────────────────────────────────

  const handleSelectEvent = useCallback((resource: CalendarJobResource) => {
    setSelectedDocId(resource.firestoreDocId);
    setSelectedIsScheduler(resource.role === "scheduler");
    // Clear any open edit panels when the user selects a new event
    setScheduleJobPanelMode(null);
    setSelectWerkersJobId(null);
    setSelectWerkersSeriesState(null);
  }, []);

  const handleEditRequest = useCallback((jobDocId: string) => {
    setEditingJobId(jobDocId);
    setScheduleJobPanelMode("edit");
    setSelectWerkersJobId(null);
    setSelectWerkersSeriesState(null);
  }, []);

  const handleEditJobComplete = useCallback((result?: EditCompleteResult) => {
    setScheduleJobPanelMode(null);

    if (result?.nextView === "selectWerkers" && result.jobId) {
      setSelectedDocId(result.jobId);
      setSelectWerkersSeriesState(null);
      setSelectWerkersJobId(result.jobId);
      return;
    }

    if (result?.nextView === "selectWerkersSeries" && result.seriesId && result.jobIds?.length) {
      if (result.jobIds[0]) setSelectedDocId(result.jobIds[0]);
      setSelectWerkersJobId(null);
      setSelectWerkersSeriesState({ seriesId: result.seriesId, jobIds: result.jobIds });
      return;
    }

    if (result?.jobId) setSelectedDocId(result.jobId);
    setSelectWerkersJobId(null);
    setSelectWerkersSeriesState(null);
  }, []);

  const handleEditWerkersRequest = useCallback(
    (result: { nextView: "selectWerkers" | "selectWerkersSeries"; jobId?: string; seriesId?: string }) => {
      setScheduleJobPanelMode(null);

      if (result.nextView === "selectWerkers" && result.jobId) {
        setSelectedDocId(result.jobId);
        setSelectWerkersSeriesState(null);
        setSelectWerkersJobId(result.jobId);
        return;
      }

      if (result.nextView === "selectWerkersSeries" && result.seriesId) {
        setSelectWerkersJobId(null);
        setSelectWerkersSeriesState({ seriesId: result.seriesId, jobIds: undefined });
      }
    },
    [],
  );

  const handleSelectWerkersComplete = useCallback((result?: WerkersCompleteResult) => {
    setSelectWerkersJobId(null);
    if (result?.jobId) setSelectedDocId(result.jobId);
  }, []);

  const handleSelectWerkersSeriesComplete = useCallback((result?: WerkersCompleteResult) => {
    setSelectWerkersSeriesState(null);
    if (result?.jobId) setSelectedDocId(result.jobId);
  }, []);

  const handleJobDeleted = useCallback(() => {
    setSelectedDocId(null);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedDocId(null);
  }, []);

  // Past jobs are paginated; fetch the next page once the visible range
  // reaches the oldest job already loaded.
  const handleVisibleDateChange = useCallback((date: Date) => {
    if (!hasMorePast || !earliestPastJobDate) return;
    if (date.toISOString() <= earliestPastJobDate) {
      loadMorePast();
    }
  }, [hasMorePast, earliestPastJobDate, loadMorePast]);

  // ─── Guards ───────────────────────────────────────────────────────────────
  if (!authReady) return <LoadingScreen />;
  if (!currentUser) return <Redirect to={{ pathname: "/login", search: "?returnUrl=/portal" }} />;

  // ─── Right-pane renderer ─────────────────────────────────────────────────
  const renderRightPane = () => {
    if (scheduleJobPanelMode === "edit" && editingJobId) {
      return (
        <div style={{ height: "100%", overflow: "auto" }}>
          <ScheduleJobDesktopWrapper
            mode="edit"
            jobDocId={editingJobId}
            onComplete={handleEditJobComplete}
          />
        </div>
      );
    }

    if (selectWerkersJobId) {
      return (
        <div style={{ height: "100%", overflow: "auto" }}>
          <SelectWerkersDesktopWrapper
            jobId={selectWerkersJobId}
            onComplete={handleSelectWerkersComplete}
          />
        </div>
      );
    }

    if (selectWerkersSeriesState) {
      return (
        <div style={{ height: "100%", overflow: "auto" }}>
          <SelectWerkersSeriesDesktopWrapper
            seriesId={selectWerkersSeriesState.seriesId}
            jobIds={selectWerkersSeriesState.jobIds}
            onComplete={handleSelectWerkersSeriesComplete}
          />
        </div>
      );
    }

    if (selectedDocId) {
      return (
        <JobDetailPanel
          jobId={selectedDocId}
          isScheduler={selectedIsScheduler}
          onEditRequest={isScheduler ? handleEditRequest : undefined}
          onEditWerkersRequest={isScheduler ? handleEditWerkersRequest : undefined}
          onJobDeleted={handleJobDeleted}
        />
      );
    }

    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          opacity: 0.85,
        }}
      >
        <img
          src="../assets/GorillaJonesNoBackground.png"
          alt="WerkApe"
          style={{ width: "70%", maxWidth: 480, height: "auto", objectFit: "contain" }}
        />
        <p style={{ color: "var(--ion-color-medium)", fontSize: 14, margin: 0 }}>
          Select a job to view details
        </p>
      </div>
    );
  };

  return (
    <WebLayout title="Calendar">
      <div style={{ height: "100%", display: "flex", flexDirection: "row", overflow: "hidden" }}>

        {/* ── Left pane: Calendar ─────────────────────────────────────────── */}
        <div
          style={{
            width: "55%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderRight: "1px solid var(--ion-color-light-shade)",
            overflow: "hidden",
          }}
        >
          {isJobsLoading ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <LoadingScreen />
            </div>
          ) : (
            <CalendarView
              events={events}
              onSelectEvent={handleSelectEvent}
              onClearSelection={handleClearSelection}
              isScheduler={isScheduler}
              selectedDocId={selectedDocId}
              onVisibleDateChange={isScheduler ? handleVisibleDateChange : undefined}
            />
          )}
        </div>

        {/* ── Right pane ──────────────────────────────────────────────────── */}
        <div
          style={{
            width: "45%",
            height: "100%",
            overflowY: "auto",
          }}
        >
          {renderRightPane()}
        </div>

      </div>
    </WebLayout>
  );
};

// ─── Desktop wrapper components (same pattern as HomeDesktop) ─────────────────

const ScheduleJobDesktopWrapper: React.FC<{
  mode: "edit";
  jobDocId: string;
  onComplete: (result?: EditCompleteResult) => void;
}> = ({ mode, jobDocId, onComplete }) => (
  <ScheduleJob
    desktopMode={{
      isDesktopMode: true,
      mode,
      jobDocId,
      onComplete,
    }}
  />
);

const SelectWerkersDesktopWrapper: React.FC<{
  jobId: string;
  onComplete: (result?: WerkersCompleteResult) => void;
}> = ({ jobId, onComplete }) => (
  <SelectWerkers
    desktopMode={{
      isDesktopMode: true,
      jobId,
      onComplete,
    }}
  />
);

const SelectWerkersSeriesDesktopWrapper: React.FC<{
  seriesId: string;
  jobIds?: string[];
  onComplete: (result?: WerkersCompleteResult) => void;
}> = ({ seriesId, jobIds, onComplete }) => (
  <SelectWerkersSeries
    desktopMode={{
      isDesktopMode: true,
      seriesId,
      jobIds,
      onComplete,
    }}
  />
);

export default CalendarDesktop;
